/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CheckIcon, CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { useCoachSuggestionContext } from 'bundles/ai-coach-platform/components/patterns/suggestions/context/coachSuggestionContext';
import { type SuggestionAction } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

const styles2 = {
  suggestionFooter: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
};

type Props = {
  acceptLabel?: string;
  dismissLabel?: string;
  hideDismiss?: boolean;
};

export type CoachSuggestionFooterComponent = React.ReactElement<Props>;

const CoachSuggestionsFooter = ({
  acceptLabel = _t('Accept'),
  dismissLabel = _t('Dismiss'),
  hideDismiss = false,
}: Props) => {
  const trackingData = useCoachTrackingContext()?.data;
  const track = useTracker();
  const { id, onAction, onToggle } = useCoachSuggestionContext();

  const handleAction = React.useCallback<(action: SuggestionAction) => void>(
    (action) => {
      onToggle(false);
      onAction(action);

      if (trackingData) {
        track('interact_coach_suggestions', {
          interactionType: action,
          suggestionIds: [id],
          coach: {
            mode: trackingData.mode,
          },
          ...(trackingData.courseBuilderWorkflowId && {
            courseBuilderWorkflowId: trackingData.courseBuilderWorkflowId,
          }),
          ...(trackingData.courseBuilderWorkflowType && {
            courseBuilderWorkflowType: trackingData.courseBuilderWorkflowType,
          }),
        });
      }
    },
    [onAction, onToggle, id, track, trackingData]
  );

  return (
    <div css={styles2.suggestionFooter}>
      <CoachButton
        onClick={() => handleAction('accept')}
        variant="secondary"
        icon={<CheckIcon />}
        iconPosition="before"
        size="small"
        aria-label={_t('Accept suggestion')}
      >
        {acceptLabel}
      </CoachButton>
      {!hideDismiss && (
        <CoachButton
          onClick={() => handleAction('reject')}
          variant="ghost"
          icon={<CloseIcon />}
          iconPosition="before"
          size="small"
          aria-label={_t('Dismiss suggestion')}
        >
          {dismissLabel}
        </CoachButton>
      )}
    </div>
  );
};

export default CoachSuggestionsFooter;
